import { useSyncExternalStore } from 'react';

/**
 * Light / dark colour mode. Light is the default for everyone (the OS preference is ignored);
 * the admin's choice is remembered per browser and applied as <html data-mode="dark">.
 */
export type ColorMode = 'light' | 'dark';

const STORAGE_KEY = 'manhattan.mode';
const listeners = new Set<() => void>();

function stored(): ColorMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

let current: ColorMode = typeof window === 'undefined' ? 'light' : stored();

function apply(mode: ColorMode) {
  const root = document.documentElement;
  if (mode === 'dark') root.dataset.mode = 'dark';
  else delete root.dataset.mode;
  root.style.colorScheme = mode;
}

/** Applies the remembered mode; call before the first render to avoid a flash. */
export function applyStoredColorMode(): void {
  current = stored();
  apply(current);
}

/** Switches mode, remembers it and notifies every useColorMode(). */
export function setColorMode(mode: ColorMode): void {
  current = mode;
  apply(mode);
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current colour mode (re-renders on change). */
export function useColorMode(): ColorMode {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => 'light',
  );
}

/** The current value of a colour token (`tokenColor('accent')` → "#047a74"); undefined when unknown. */
export function tokenColor(token: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return getComputedStyle(document.documentElement).getPropertyValue(`--color-${token}`).trim() || undefined;
}

/**
 * A colour token as a raw colour for code that cannot use CSS classes (Google Maps overlays),
 * following the colour mode: `strokeColor={useTokenColor('accent')}`.
 */
export function useTokenColor(token: string): string | undefined {
  useColorMode();
  return tokenColor(token);
}
